import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Award, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

export const RestaurantLoyalty = () => {
  const { restaurant, refresh } = useAuth();
  const [list, setList] = useState([]);
  const [cfg, setCfg] = useState(restaurant?.loyalty || { enabled: true, target: 5, reward_type: "discount", reward_value: "10% off" });

  const load = () => api.get("/restaurant/loyalty").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (restaurant?.loyalty) setCfg(restaurant.loyalty); }, [restaurant]);

  const saveCfg = async () => {
    try {
      await api.patch("/restaurant/loyalty/settings", cfg);
      toast.success("Loyalty settings saved");
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const claim = async (phone) => {
    try {
      await api.post(`/restaurant/loyalty/${phone}/claim`);
      toast.success("Reward claimed");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div data-testid="rest-loyalty">
      <h1 className="text-2xl font-bold">Loyalty</h1>
      <p className="text-sm text-zq-muted mt-1">Reward repeat customers with badges.</p>

      <div className="zq-card p-5 mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="text-xs font-medium text-zq-muted">Status</label>
          <select className="zq-input" value={cfg.enabled ? "1" : "0"} onChange={(e) => setCfg({ ...cfg, enabled: e.target.value === "1" })} data-testid="loy-enabled">
            <option value="1">Enabled</option>
            <option value="0">Disabled</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-zq-muted">Badges to unlock</label>
          <input className="zq-input" type="number" min={1} value={cfg.target} onChange={(e) => setCfg({ ...cfg, target: Number(e.target.value) })} data-testid="loy-target"/>
        </div>
        <div>
          <label className="text-xs font-medium text-zq-muted">Reward type</label>
          <select className="zq-input" value={cfg.reward_type} onChange={(e) => setCfg({ ...cfg, reward_type: e.target.value })} data-testid="loy-type">
            <option value="discount">Discount %</option>
            <option value="free_item">Free Item</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-zq-muted">Reward value</label>
          <input className="zq-input" placeholder="e.g. 10% off" value={cfg.reward_value} onChange={(e) => setCfg({ ...cfg, reward_value: e.target.value })} data-testid="loy-value"/>
        </div>
        <div className="sm:col-span-2 lg:col-span-4">
          <button className="zq-btn zq-btn-primary" onClick={saveCfg} data-testid="loy-save">Save settings</button>
        </div>
      </div>

      <h3 className="font-semibold mt-8">Customer leaderboard</h3>
      <div className="zq-card mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zq-orange-light text-left">
            <tr>
              <th className="p-3">Customer</th>
              <th className="p-3">Phone</th>
              <th className="p-3">Badges</th>
              <th className="p-3">Total orders</th>
              <th className="p-3">Rewards claimed</th>
              <th className="p-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-zq-muted">No loyalty members yet.</td></tr>}
            {list.map((c) => (
              <tr key={c.phone} className="border-t border-zq-border">
                <td className="p-3 font-medium">{c.name || "—"}</td>
                <td className="p-3 text-xs">{c.phone}</td>
                <td className="p-3"><span className="zq-pill zq-pill-orange flex w-fit items-center gap-1"><Award size={12}/> {c.badges}/{cfg.target}</span></td>
                <td className="p-3">{c.total_orders}</td>
                <td className="p-3">{c.rewards_claimed}</td>
                <td className="p-3 text-right">
                  {c.badges >= cfg.target ? (
                    <button className="zq-btn zq-btn-primary !py-1.5" onClick={() => claim(c.phone)} data-testid={`claim-${c.phone}`}>
                      <CheckCircle2 size={14}/> Mark claimed
                    </button>
                  ) : (
                    <span className="text-xs text-zq-muted">— </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
